import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import InteractiveMap from "@/components/InteractiveMap";
import RailwayDivider from "@/components/RailwayDivider";
import SEO from "@/components/SEO";
import { Footprints, Bike, Droplet, Sun, Mountain, Wind } from "lucide-react";

const PageHero = ({ eyebrow, title, subtitle }) => (
  <section className="pt-40 pb-16 bg-pietra-light border-b border-carrubo/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="eyebrow mb-6">{eyebrow}</div>
      <h1 className="font-serif text-5xl md:text-7xl text-grafite mb-4">{title}</h1>
      <p className="text-lg text-grafite-soft max-w-3xl">{subtitle}</p>
    </div>
  </section>
);

const Cammino = () => {
  const { t } = useLang();
  const [mapData, setMapData] = useState(null);

  useEffect(() => {
    api.get("/mappa").then((r) => setMapData(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="cammino-page">
      <SEO
        titleIt="Il Cammino — Panoramica, mappa interattiva e preparazione"
        titleEn="The Trail — Overview, interactive map and preparation"
        descriptionIt="Panoramica del Cammino Ibleo UNESCO: 285 km tra Catania e Ragusa lungo le ex-ferrovie, mappa interattiva delle stazioni, consigli di preparazione a piedi e in bici."
        descriptionEn="Overview of the Cammino Ibleo UNESCO: 285 km between Catania and Ragusa along the disused railways, interactive station map, walking and cycling preparation tips."
      />
      <PageHero eyebrow="Sicilia Orientale" title={t("cammino_title")} subtitle={t("cammino_subtitle")} />

      <section className="py-20 bg-pietra-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 items-start">
            <div className="md:col-span-1">
              <h2 className="font-serif text-3xl md:text-4xl text-grafite mb-4">{t("cammino_pano_title")}</h2>
              <RailwayDivider className="mb-6" />
            </div>
            <div className="md:col-span-2 space-y-6 text-grafite-soft text-lg leading-relaxed">
              <p>
                Il Cammino Ibleo si sviluppa su circa 40 km lungo il tracciato dell'ex ferrovia Siracusa–Vizzini–Ragusa,
                dismessa nel 1956. Il percorso attraversa l'altopiano ibleo, si tuffa nelle cave di calcare, sfiora
                sette centri iscritti al patrimonio UNESCO.
              </p>
              <p>
                Sei tappe di lunghezza contenuta (5-8 km) permettono al camminatore di percorrerlo in 4-5 giorni,
                al cicloturista in 1-2. Le ex-stazioni ferroviarie funzionano da punti di sosta e da timbro della credenziale.
              </p>
              <div className="grid grid-cols-3 gap-6 pt-6">
                {[
                  { icon: Mountain, val: "0-680", label: "m s.l.m." },
                  { icon: Sun, val: "9-11", label: "mesi ideali" },
                  { icon: Wind, val: "Sud-Est", label: "esposizione" },
                ].map((s, i) => (
                  <div key={i} className="p-4 bg-pietra rounded-sm">
                    <s.icon size={20} className="text-carrubo mb-2" strokeWidth={1.5} />
                    <div className="font-serif text-2xl text-grafite">{s.val}</div>
                    <div className="text-xs uppercase tracking-widest text-grafite-soft">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-pietra">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-10">
            <div className="eyebrow mb-3">Mappa</div>
            <h2 className="font-serif text-4xl md:text-5xl text-grafite mb-3">{t("cammino_map_title")}</h2>
            <p className="text-grafite-soft max-w-3xl">{t("cammino_map_intro")}</p>
          </div>
          {mapData && (
            <InteractiveMap
              polyline={mapData.polyline}
              polylineSouth={mapData.polyline_south}
              polylineKamarina={mapData.polyline_kamarina}
              stations={mapData.stazioni}
              height="600px"
            />
          )}
        </div>
      </section>

      <section className="py-20 bg-pietra-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <div className="eyebrow mb-3">Preparazione</div>
            <h2 className="font-serif text-4xl md:text-5xl text-grafite">{t("cammino_prep_title")}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-10 bg-pietra rounded-sm border border-carrubo/10">
              <Footprints size={36} className="text-carrubo mb-6" strokeWidth={1.3} />
              <h3 className="font-serif text-2xl text-grafite mb-4">{t("prep_walk")}</h3>
              <p className="text-grafite-soft leading-relaxed">{t("prep_walk_body")}</p>
              <ul className="mt-6 space-y-2 text-sm text-grafite-soft">
                <li className="flex items-center gap-2"><Droplet size={14} className="text-cielo" /> 1,5 L acqua minima</li>
                <li className="flex items-center gap-2"><Sun size={14} className="text-miele" /> Cappello e crema solare</li>
                <li className="flex items-center gap-2"><Mountain size={14} className="text-carrubo" /> Trekking leggero</li>
              </ul>
            </div>
            <div className="p-10 bg-pietra rounded-sm border border-carrubo/10">
              <Bike size={36} className="text-carrubo mb-6" strokeWidth={1.3} />
              <h3 className="font-serif text-2xl text-grafite mb-4">{t("prep_bike")}</h3>
              <p className="text-grafite-soft leading-relaxed">{t("prep_bike_body")}</p>
              <ul className="mt-6 space-y-2 text-sm text-grafite-soft">
                <li>· Gravel o MTB front</li>
                <li>· Copertoni ≥ 40 mm</li>
                <li>· Casco + luci gallerie</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cammino;
